import { WalletType } from 'types/wallet';
import identity from 'utils/identity';

import {
  CreateVentureBuilderPayload,
  EditVentureBuilderPayload,
  ManagedStartupApiType,
  ManagedStartupType,
  VentureBuilderApiType,
  VentureBuilderEditRequestType,
  VentureBuilderType,
} from '../types';

const toFlag = (value: boolean): 0 | 1 => (value ? 1 : 0);

export const convertVentureBuilderResponse = (vb: VentureBuilderApiType): VentureBuilderType => {
  const firstName = vb.firstName || '';
  const lastName = vb.lastName || '';

  return {
    id: vb.id,
    name: vb.name || '',
    description: vb.description || '',
    firstName,
    lastName,
    fullName: `${firstName} ${lastName}`.trim(),
    email: vb.email || '',
    active: Boolean(vb.active),
    walletAmount: vb.walletAmount || 0,
    userId: vb.userID || vb.user?.id || 0,
    hasLoggedIn: identity.isTruthyString(vb.user?.lastLogin),
  };
};

export const convertManagedStartupResponse = (startup: ManagedStartupApiType): ManagedStartupType => ({
  id: startup.id,
  name: startup.name || '',
  email: startup.email || '',
  ventureBuilderId: startup.ventureBuilderID,
  walletAmount: startup.walletAmount || 0,
  wallet: identity.isTruthyNumber(startup.wallet?.id)
    ? {
      total: startup.wallet!.totalWalletAmount || 0,
      balance: startup.wallet!.balance || 0,
      blocked: startup.wallet!.blockedAmount || 0,
      consumed: startup.wallet!.consumedAmount || 0,
    }
    : null,
  active: Boolean(startup.active),
  createdAt: startup.createdAt || null,
});

// Local integration contract: startup objects keep each name paired with its contact.
// Confirm this encoding with the backend owner before connecting to the shared API.
export const convertCreateVentureBuilderRequest = (payload: CreateVentureBuilderPayload): FormData => {
  const formData = new FormData();
  formData.append('_name', payload.name.trim());
  formData.append('_description', payload.description.trim());
  formData.append('_first_name', payload.firstName.trim());
  formData.append('_last_name', payload.lastName.trim());
  formData.append('_email', payload.email.trim());
  formData.append('_active', String(payload.active));
  formData.append('_startups', JSON.stringify(payload.startups.map(({ name, email }) => ({
    name: name.trim(), email: email.trim(),
  }))));
  return formData;
};

export const convertEditVentureBuilderRequest = (
  payload: EditVentureBuilderPayload,
): VentureBuilderEditRequestType => ({
  ...(payload.description === undefined ? {} : { _description: payload.description.trim() }),
  ...(payload.active === undefined ? {} : { _active: toFlag(payload.active) }),
});

export const toStartupWallet = (wallet: {
  balance: number, total: number, consumed: number, blocked: number,
}): WalletType => ({
  balance: String(wallet.balance),
  totalWalletAmount: String(wallet.total),
  consumedAmount: String(wallet.consumed),
  blockedAmount: String(wallet.blocked),
});

export const startupLabel = (startup: ManagedStartupType) => `${startup.name} — ${startup.email}`;
